import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { timeline, timelineStates } from '@/mocks/home';
import { cdn } from '@/config/cdn';
import TimelineList from './TimelineList';

gsap.registerPlugin(ScrollTrigger);

/** Node centres as % of track width. */
const pos = (i: number, n: number) => (i / (n - 1)) * 90 + 5;
const HOLD = 0.35; // rest at start and end
const DWELL = 0.75; // landing rest at each intermediate node
const SEG = 1; // travel duration per segment
const LIFT = 130; // px, apex of each toss
const ROLL = 360; // deg, one full forward roll per segment

/**
 * Desktop (lg+, motion allowed): tall runway with a CSS-sticky 100vh stage.
 * One scrubbed master timeline: 4 segments (node 1→2→3→4→5).
 * Each segment = lift + forward roll → travel along an arc → settle + land,
 * followed by a short dwell so every node reads as a landing point.
 * Frame swaps happen on descent, just before landing.
 */
export default function Timeline() {
  const n = timeline.length;
  const runwayRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const pastryRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const frameRefs = useRef<(HTMLImageElement | null)[]>([]);
  const stopRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const runway = runwayRef.current;
    const track = trackRef.current;
    const pastry = pastryRef.current;
    const progress = progressRef.current;
    const frames = frameRefs.current.filter((f): f is HTMLImageElement => !!f);
    const stops = stopRefs.current;
    if (!runway || !track || !pastry || !progress || frames.length !== timelineStates.length) return undefined;

    const xAt = (i: number) => (track.offsetWidth * pos(i, n)) / 100 - pastry.offsetWidth / 2;
    const segStart = (k: number) => HOLD + k * (SEG + DWELL);
    let lastActive = -1;
    const setActive = (a: number) => {
      if (a === lastActive) return;
      lastActive = a;
      stops.forEach((s, i) => {
        if (!s) return;
        s.dataset.active = String(i === a);
        s.dataset.past = String(i <= a);
      });
    };
    const showFrame = (f: number) => gsap.set(frames, { opacity: (i: number) => (i === f ? 1 : 0) });

    const mm = gsap.matchMedia();
    mm.add(
      { desktop: '(min-width: 1024px)', reduce: '(prefers-reduced-motion: reduce)' },
      (ctx) => {
        const { desktop, reduce } = ctx.conditions as { desktop: boolean; reduce: boolean };
        if (!desktop) return undefined;

        // Reduced motion: CSS swaps to the static 5-state list; no animation here.
        if (reduce) return undefined;

        showFrame(0);
        const stage = stageRef.current;
        if (!stage) return undefined;
        // One master timeline: pinned stage, lightly smoothed scrub, snaps to node landings.
        gsap.set(pastry, { force3D: true });
        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: stage,
            pin: stage,
            start: 'top top',
            end: () => `+=${window.innerHeight * 3}`,
            scrub: 1,
            invalidateOnRefresh: true,
            snap: {
              snapTo: 'labels',
              duration: { min: 0.8, max: 1.6 },
              delay: 0.15,
              inertia: false,
              ease: 'sine.inOut',
              directional: true,
            },
          },
          onUpdate: () => {
            const t = tl.time();
            let a = 0;
            for (let k = 0; k < n - 1; k += 1) if (t >= segStart(k) + SEG * 0.97) a = k + 1;
            setActive(a);
          },
        });

        tl.fromTo(pastry, { x: () => xAt(0), y: 0, rotation: 0 }, { x: () => xAt(0), duration: HOLD }, 0);
        tl.fromTo(progress, { scaleX: pos(0, n) / 100 }, { scaleX: pos(0, n) / 100, duration: HOLD }, 0);

        for (let k = 0; k < n - 1; k += 1) {
          const at = segStart(k);
          const to = k + 1;
          // horizontal travel: eased so it departs and arrives softly
          // toss: near-constant horizontal speed in flight (like a thrown object)
          tl.to(pastry, { x: () => xAt(to), duration: SEG, ease: 'sine.inOut' }, at);
          // gravity arc: decelerate up, accelerate down
          tl.to(pastry, { y: -LIFT, duration: SEG * 0.5, ease: 'power2.out' }, at);
          tl.to(pastry, { y: 0, duration: SEG * 0.5, ease: 'power2.in' }, at + SEG * 0.5);
          // one even spin during flight, finishing exactly at touchdown
          tl.fromTo(pastry, { rotation: ROLL * k }, { rotation: ROLL * to, duration: SEG, ease: 'sine.inOut', immediateRender: false }, at);
          // soft landing: tiny squash and recover
          tl.to(pastry, { scaleY: 0.95, scaleX: 1.03, duration: 0.06, ease: 'power1.out' }, at + SEG);
          tl.to(pastry, { scaleY: 1, scaleX: 1, duration: 0.12, ease: 'power1.inOut' }, at + SEG + 0.06);
          tl.to(progress, { scaleX: pos(to, n) / 100, duration: SEG, ease: 'power2.inOut' }, at);

          const fromF = timeline[k].frame;
          const toF = timeline[to].frame;
          if (toF !== fromF) {
            // swap only after landing (inside the dwell / final hold)
            tl.fromTo(frames[toF], { opacity: 0 }, { opacity: 1, duration: 0.12, immediateRender: false }, at + SEG + 0.02);
            tl.fromTo(frames[fromF], { opacity: 1 }, { opacity: 0, duration: 0.01, immediateRender: false }, at + SEG + 0.14);
          }
          tl.to({}, { duration: k < n - 2 ? DWELL : 0.2 }, at + SEG);
        }
        tl.to({}, { duration: HOLD });
        setActive(0);

        // Snap points: start, each node right after landing + frame swap, and the end.
        tl.addLabel('n0', 0);
        for (let i = 1; i < n; i += 1) tl.addLabel(`n${i}`, segStart(i - 1) + SEG + 0.2);
        tl.addLabel('end', tl.duration());

        return () => {
          tl.scrollTrigger?.kill();
          tl.kill();
          gsap.set([pastry, progress, ...frames], { clearProps: 'transform,opacity' });
        };
      },
    );

    let cancelled = false;
    Promise.all(
      frames.map((f) => (f.complete ? Promise.resolve() : new Promise<void>((r) => {
        f.addEventListener('load', () => r(), { once: true });
        f.addEventListener('error', () => r(), { once: true });
      }))),
    ).then(() => { if (!cancelled) ScrollTrigger.refresh(); });

    return () => {
      cancelled = true;
      mm.revert();
    };
  }, [n]);

  return (
    <section className="bg-[#F5F1EB] border-t border-[rgba(23,21,21,0.08)]" aria-labelledby="timeline-title">
      <div ref={runwayRef} className="relative" data-timeline-runway>
        <div ref={stageRef} className="bg-[#F5F1EB] py-24 md:py-40 lg:motion-safe:h-screen lg:motion-safe:py-0 lg:motion-safe:pt-24 lg:motion-safe:flex lg:motion-safe:flex-col lg:motion-safe:justify-center">
          <div className="fold-shell w-full">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
              <h2 id="timeline-title" className="fold-serif uppercase md:col-span-8 text-[12vw] md:text-[7vw] xl:text-[6.5rem] lg:motion-safe:text-[min(6.5rem,10vh)] leading-[0.9]">
                A morning,<br />
                <span className="italic">made in layers.</span>
              </h2>
              <p className="md:col-span-3 md:col-start-10 text-sm leading-relaxed text-[#5d5853]">
                From the first cold fold at five to the last crumb at three. One bake, one day, nothing kept overnight.
              </p>
            </div>

            <TimelineList />

            <div ref={trackRef} className="hidden lg:block lg:motion-reduce:hidden relative w-full h-[420px] mt-14 xl:mt-20" data-timeline-track>
              <div className="absolute inset-x-0 top-0 flex justify-between fold-label text-[#8A8580]">
                <span>Begin · Whole</span>
                <span>Mid-morning · Half</span>
                <span>End · Crumbs</span>
              </div>

              <div className="absolute left-0 right-0 top-[290px] h-px bg-[rgba(23,21,21,0.3)]"></div>
              <div ref={progressRef} className="absolute left-0 top-[289px] w-full h-[3px] bg-[#171515] origin-left will-change-transform" style={{ transform: `scaleX(${pos(0, n) / 100})` }} data-timeline-progress></div>
              <span className="absolute left-0 top-[282px] w-px h-[17px] bg-[#171515]"></span>
              <span className="absolute right-0 top-[282px] w-px h-[17px] bg-[#171515]"></span>

              {/* Cutout content bottom sits at ~76% of the box; box positioned so it lands just above the node line. */}
              <div
                ref={pastryRef}
                className="absolute left-0 top-[182px] w-[170px] h-[128px] xl:w-[184px] xl:h-[138px] xl:top-[174px] will-change-transform pointer-events-none z-10"
                style={{ transformOrigin: '50% 49%' }}
                aria-hidden="true"
                data-timeline-pastry
              >
                {timelineStates.map((s, i) => (
                  <img
                    key={s.key}
                    ref={(el) => { frameRefs.current[i] = el; }}
                    src={cdn(s.src)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain"
                    style={{ opacity: i === 0 ? 1 : 0, zIndex: i + 1 }}
                  />
                ))}
              </div>

              <ol>
                {timeline.map((t, i) => (
                  <li key={t.time}>
                    <div
                      ref={(el) => { stopRefs.current[i] = el; }}
                      className="group absolute top-0 bottom-0 -translate-x-1/2 w-[160px]"
                      style={{ left: `${pos(i, n)}%` }}
                      data-active={String(i === 0)}
                      data-past={String(i === 0)}
                    >
                      <span className="absolute left-1/2 -translate-x-1/2 top-[283px] w-[15px] h-[15px] rounded-full border border-[#171515] bg-[#F5F1EB] transition-[background-color,transform] duration-200 group-data-[past=true]:bg-[#171515] group-data-[active=true]:scale-125"></span>
                      <span className="absolute left-1/2 -translate-x-1/2 top-[277px] w-[27px] h-[27px] rounded-full border border-[#171515] opacity-0 transition-opacity duration-200 group-data-[active=true]:opacity-100"></span>
                      <div className="absolute top-[320px] left-0 right-0 text-center">
                        <p className="fold-label text-[#a9a39c]">0{i + 1}</p>
                        <p className="mt-1 fold-serif text-[32px] tabular-nums text-[#8A8580] transition-colors duration-200 group-data-[active=true]:text-[#171515]">{t.time}</p>
                        <p className="mt-1 text-xs leading-snug text-[#5d5853] px-2">{t.label}</p>
                        <p className="mt-2 fold-label text-[#a9a39c] transition-colors duration-200 group-data-[active=true]:text-[#171515]">{t.state}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}